import { useEffect, useState } from "react";
import { Box, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import { notifications } from "@mantine/notifications";
import { Panel, Prompt, Screen, VfToggle, LoadError } from "../components/primitives";
import { useConsoleStore } from "../stores/consoleStore";
import { useUiStore } from "../stores/uiStore";
import { useWorkspaceStore } from "../stores/workspaceStore";
import { PALETTES, PALETTE_KEYS, type PaletteKey } from "../theme/palettes";
import css from "./screens.module.css";

export default function Settings() {
  const workspaceId = useWorkspaceStore((s) => s.currentId);
  const settings = useConsoleStore((s) => s.settings);
  const loadError = useConsoleStore((s) => s.errors.settings);
  const profiles = useConsoleStore((s) => s.profiles);
  const loadSettings = useConsoleStore((s) => s.loadSettings);
  const loadProfiles = useConsoleStore((s) => s.loadProfiles);
  const saveSettings = useConsoleStore((s) => s.saveSettings);

  const palette = useUiStore((s) => s.palette);
  const setPalette = useUiStore((s) => s.setPalette);

  const [name, setName] = useState("");

  useEffect(() => {
    if (workspaceId) void loadSettings();
    if (profiles.length === 0) void loadProfiles();
  }, [workspaceId, loadSettings, profiles.length, loadProfiles]);

  useEffect(() => {
    if (settings) setName(settings.workspaceName);
  }, [settings]);

  const toggles = [
    {
      key: "authEnabled" as const,
      label: "Authentication",
      desc: "Require API key / Firebase / JWT on every request",
    },
    {
      key: "rateLimit" as const,
      label: "Rate limiting",
      desc: "Per-user token bucket · 429 on exceed",
    },
    {
      key: "codeSearch" as const,
      label: "Code search (/v1/code)",
      desc: "git clone, tree-sitter chunking & GitHub webhooks",
    },
  ];

  const confirmDelete = () =>
    modals.openConfirmModal({
      title: "delete workspace",
      labels: { confirm: `delete ${workspaceId}`, cancel: "cancel" },
      confirmProps: { color: "red" },
      children: (
        <Box fz="12.5px" c="var(--vf-sec)" lh={1.7}>
          <Box c="var(--vf-bad)" mb={10}>$ vf workspace delete --force</Box>
          Delete {workspaceId} along with every namespace, document, API key and repo connection it
          owns. Members lose access instantly. This cannot be undone.
        </Box>
      ),
      onConfirm: () =>
        notifications.show({
          message: "workspace deletion has no endpoint yet — see ROADMAP.md",
          color: "yellow",
        }),
    });

  return (
    <Screen>
      <Prompt command="vf config edit" />
      <LoadError message={loadError} />

      <Box className={css.settingsSection}>
        <Box mb={16}>
          <Panel title="general">
            <Box p={16}>
              <div className={css.fieldLabel}>// workspace name</div>
              <input
                className={css.textInput}
                style={{ marginBottom: 16 }}
                value={name}
                aria-label="workspace name"
                onChange={(e) => setName(e.currentTarget.value)}
                onBlur={() => {
                  if (settings && name !== settings.workspaceName) {
                    void saveSettings({ workspaceName: name });
                  }
                }}
              />

              <div className={css.fieldLabel}>// default embedder_profile</div>
              <select
                className={css.nativeSelect}
                style={{ width: 280, marginBottom: 16, padding: 9 }}
                value={settings?.defaultEmbedder ?? ""}
                aria-label="default embedder profile"
                onChange={(e) => void saveSettings({ defaultEmbedder: e.currentTarget.value })}
              >
                {profiles.map((p) => (
                  <option key={p.name} value={p.name}>
                    {p.name}
                  </option>
                ))}
              </select>

              <div className={css.fieldLabel}>// console theme</div>
              <select
                className={`${css.nativeSelect} ${css.nativeSelectAcc}`}
                style={{ width: 280 }}
                value={palette}
                aria-label="console theme"
                onChange={(e) => setPalette(e.currentTarget.value as PaletteKey)}
              >
                {PALETTE_KEYS.map((k) => (
                  <option key={k} value={k}>
                    ◈ {PALETTES[k].name}
                  </option>
                ))}
              </select>
            </Box>
          </Panel>
        </Box>

        <Box mb={16}>
          <Panel title="security & limits">
            <Box p="8px 16px">
              {toggles.map((t) => (
                <div key={t.key} className={css.toggleRow}>
                  <div>
                    <div className={css.toggleLabel}>{t.label}</div>
                    <div className={css.toggleDesc}>{t.desc}</div>
                  </div>
                  <VfToggle
                    checked={settings?.[t.key] ?? false}
                    aria-label={t.label}
                    onChange={(next) => void saveSettings({ [t.key]: next })}
                  />
                </div>
              ))}

              <Box py={13} fz="12.5px" style={{ lineHeight: 2 }}>
                <ReadOnly k="CORS allowlist" v={settings?.corsAllowlist ?? "—"} />
                <ReadOnly k="ingest backend" v={settings?.ingestBackend ?? "—"} />
                <ReadOnly k="auth chain" v={settings?.authChain ?? "—"} />
              </Box>
            </Box>
          </Panel>
        </Box>

        <div className={css.dangerZone}>
          <div className={css.dangerHead}>danger zone</div>
          <div className={css.dangerBody}>
            <Text fz="12.5px" c="var(--vf-sec)">
              Delete this workspace and every namespace it owns. This cannot be undone.
            </Text>
            <button
              type="button"
              className={css.dangerBtn}
              style={{ whiteSpace: "nowrap", padding: "9px 15px" }}
              onClick={confirmDelete}
            >
              delete workspace
            </button>
          </div>
        </div>
      </Box>
    </Screen>
  );
}

function ReadOnly({ k, v }: { k: string; v: string }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
      <span style={{ color: "var(--vf-dim)" }}>{k}</span>
      <span style={{ color: "var(--vf-text)" }}>{v}</span>
    </div>
  );
}
